import { TabLayoutContent, TabLayoutBreadcrumb } from "@/components/tab-layout";
import * as React from "react";
import { useLoaderData } from "react-router-dom";
import Awaited from "@/components/awaited";
import { IAdminCompanyLoaderData } from "./loader";
import CompanyRow from "./(blocks)/company-row";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import RefPortal from "@/components/ref-portal";
import CheckboxDropdown from "@/components/checkbox-dropdown";
import { useColumns } from "@/hooks/useColumns";
import { useRouteNavigation } from "@/hooks/use-route-navigation";
import { useRevalidateCache } from "@/hooks/use-invalidate";
import { listCompanies } from "@/api/companies";
import { CompaniesTableColumns } from "./(blocks)/columns";
import { entries, fromEntries } from "@/lib/entries";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { IntegerState, useSearchState } from "@/hooks/use-search-state";
import { normalize } from "@/lib/format";
import SmartPagination from "@/components/smart-pagination";

export interface IAdminCompanyPageProps {}

const limit = 20;
export default function AdminCompanyPage(props: IAdminCompanyPageProps) {
  const data = useLoaderData() as IAdminCompanyLoaderData;
  const navigation = useRouteNavigation("/app/admin/companies");
  useRevalidateCache(listCompanies);
  const [columns, columnsState, setColumnState] = useColumns(
    "companies",
    CompaniesTableColumns,
  );
  const [setSearch, search] = useSearchState<string>({
    key: "query",
    parse: normalize,
    serialize: normalize,
    delay: 300,
  });
  const [setOffset, offset] = useSearchState({
    key: "offset",
    defaultValue: 0,
    ...IntegerState,
  });
  const page = offset / limit;
  function setPage(newPage: number) {
    setOffset(newPage * limit);
  }
  return (
    <TabLayoutContent size="full-width">
      <TabLayoutBreadcrumb>Entreprises</TabLayoutBreadcrumb>
      <RefPortal selector="#tab-layout-head-outlet">
        <Button>Créer une entreprise</Button>
      </RefPortal>

      <div className="flex items-center gap-2 w-full">
        <Input
          className="w-96 shrink-0 bg-card"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher..."
        />
        <Awaited
          loading={navigation.state}
          resolve={data.companies}
          fallback={<Skeleton className="h-4 w-20 shrink-0" />}
          render={({ length }) => (
            <span className="text-sm text-muted-foreground shrink-0 ">
              {length} résultat{length !== 1 ? "s" : ""}
            </span>
          )}
        />
        <div className="filler" />
        <CheckboxDropdown
          items={Object.values(columns)}
          values={fromEntries(
            entries(columnsState).map(([k, s]) => [k, s.visible]),
          )}
          onChange={(column, visible) =>
            setColumnState({ state: { [column]: { visible } } })
          }
        >
          <Button variant="outline" size="sm" className="border-dashed">
            Colonnes
          </Button>
        </CheckboxDropdown>
      </div>
      <ScrollArea className="overflow-hidden relative border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              {entries(columns)
                .filter(([key]) => columnsState[key].visible)
                .map(([_, { value, header: Header }]) => (
                  <Header key={value} />
                ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <Awaited
              resolve={data.companies}
              render={(companies) =>
                companies
                  .slice(offset, offset + limit)
                  .map((company) => (
                    <CompanyRow
                      key={company.id}
                      company={company}
                      columnState={columnsState}
                    />
                  ))
              }
            />
          </TableBody>
        </Table>
      </ScrollArea>
      <Awaited
        loading={navigation.state}
        resolve={data.companies}
        fallback={
          <Skeleton className="h-10 w-[430px] mx-auto max-w-screen shrink" />
        }
      >
        {({ length }) => (
          <SmartPagination
            count={length}
            page={page}
            setPage={setPage}
            perPage={limit}
          />
        )}
      </Awaited>
    </TabLayoutContent>
  );
}
